// BulkStrategyMonitor.jsx - WITH IMPROVED COMPLETION NOTIFICATIONS & OFFLINE FIX
// + PENDING FILTER, HOT STRATEGY SORT, TODAY'S TRADES (Option B)
// + CONNECTION GRACE PERIOD FIX (prevents OFFLINE flash on page load)
// + OPTIMISTIC STAT CARD UPDATES (Open count syncs with notification cards)
// + POST-EXIT STATE UPDATE FIX (allows completed/idle transitions from open)
// + FIX: open/pending strategies always visible regardless of timeframe filter
import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Paper, Stack, Chip, IconButton, Button, Grid,
  CircularProgress, Tooltip, Typography, alpha, Avatar,
  Collapse, Alert, AlertTitle, ToggleButtonGroup, ToggleButton, Badge,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
} from '@mui/material';
import {
  History as HistoryIcon, Timeline as TimelineIcon,
  Refresh as RefreshIcon, Circle as DotIcon,
  Dashboard as DashboardIcon, Bolt as BoltIcon,
  CloudDone as CloudDoneIcon, Speed as SpeedIcon, Sync as SyncIcon,
  CheckCircle as CheckCircleIcon, Warning as WarningIcon,
  Storage as StorageIcon, CloudOff as CloudOffIcon, WifiOff as WifiOffIcon,
  Schedule as ScheduleIcon, LocalFireDepartment as FireIcon,
  ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon,
  EmojiEvents as TrophyIcon, AccountBalanceWallet as WalletIcon,
} from '@mui/icons-material';

import { BulkStrategyCard } from './components/BulkStrategyCard';
import { StatCard } from './components/StatCard';
import { DataCache, RequestManager } from './utils/cache';
import { StrategyApiService } from './services/strategyApi';
import { useWebSocket } from './hooks/useWebSocket';
import { isStrategyActiveNow } from './utils/timeRestriction';

// ==================== ISOLATED NY CLOCK (prevents full tree re-render) ====================
const NYClock = React.memo(() => {
  const [time, setTime] = useState('');
  
  useEffect(() => {
    const updateTime = () => {
      try {
        setTime(new Intl.DateTimeFormat('en-US', {
          timeZone: 'America/New_York',
          hour: 'numeric', minute: '2-digit', second: '2-digit',
          hour12: true, timeZoneName: 'short',
        }).format(new Date()));
      } catch (e) { /* ignore */ }
    };
    updateTime();
    const i = setInterval(updateTime, 1000);
    return () => clearInterval(i);
  }, []);

  return <strong style={{ color: '#00d4ff' }}>{time}</strong>;
});
NYClock.displayName = 'NYClock';

// ==================== CONSTANTS ====================
const PENDING_THRESHOLD = 25;

const BulkStrategyMonitor = ({ 
  apiBaseUrl = window.location.origin,
  onInitialLoadComplete,
  onLoadError 
}) => {
  const navigate = useNavigate();
  
  // ==================== STATE ====================
  const [bulkStrategies, setBulkStrategies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const [lastSync, setLastSync] = useState(null);
  const [expandedCards, setExpandedCards] = useState({});
  const [liveUpdates, setLiveUpdates] = useState([]);
  const [timeframeFilter, setTimeframeFilter] = useState('all');
  const [positionFilter, setPositionFilter] = useState(false);
  const [pendingFilter, setPendingFilter] = useState(false);
  const [statusCounts, setStatusCounts] = useState(null);
  const [strategyBroadcasts, setStrategyBroadcasts] = useState({});
  const [broadcastsLoading, setBroadcastsLoading] = useState({});
  const [strategyTrades, setStrategyTrades] = useState({});
  const [tradesLoading, setTradesLoading] = useState({});
  const [isOffline, setIsOffline] = useState(false);
  const [analyzerSummary, setAnalyzerSummary] = useState(null);
  const [initializing, setInitializing] = useState(true);
  
  // ==================== CONNECTION GRACE PERIOD STATE ====================
  const [connectionGrace, setConnectionGrace] = useState(true);
  const hasEverConnected = useRef(false);

  // ==================== TODAY'S TRADES STATE ====================
  const [todaysTrades, setTodaysTrades] = useState([]);
  const [todaysSummary, setTodaysSummary] = useState(null);
  const todaysTradesLoadingRef = useRef(false);
  const [todaysTradesExpanded, setTodaysTradesExpanded] = useState(true);
  const todaysTradesLoadedOnce = useRef(false);
  const prevTradesSummaryRef = useRef(null);
  const liveUpdatesRef = useRef([]);

  // ==================== SERVICE REFS ====================
  const dataCache = useRef(new DataCache(60000));
  const requestManager = useRef(new RequestManager());
  const apiService = useRef(null);
  const wsUpdateTimeoutRef = useRef(null);
  const lastSuccessfulLoadRef = useRef(null);
  const initialLoadRef = useRef(false);
  const mountedRef = useRef(true);
  const initializationTimeoutRef = useRef(null);
  const hasDataRef = useRef(false);
  const loadingRef = useRef(true);

  // ==================== PERFORMANCE TIMING REFS ====================
  const componentMountTime = useRef(null);
  const loadStartTime = useRef(null);
  const wsMessageTimes = useRef([]);
  const stateUpdateTimes = useRef([]);

  // ==================== COMPONENT MOUNT TIMING ====================
  useEffect(() => {
    componentMountTime.current = performance.now();
    console.log('[MONITOR-PERF] 🎬 Component mounted at', new Date().toISOString());
    
    return () => {
      const totalLifetime = performance.now() - componentMountTime.current;
      console.log(`[MONITOR-PERF] 🏁 Component unmounted after ${totalLifetime.toFixed(2)}ms`);
      
      if (wsMessageTimes.current.length > 0) {
        const avgWsTime = wsMessageTimes.current.reduce((a, b) => a + b, 0) / wsMessageTimes.current.length;
        console.log(`[MONITOR-PERF] 📊 WS Messages: ${wsMessageTimes.current.length}, Avg: ${avgWsTime.toFixed(2)}ms`);
      }
      
      mountedRef.current = false;
      
      if (initializationTimeoutRef.current) {
        clearTimeout(initializationTimeoutRef.current);
      }
    };
  }, []);

  // ==================== CONNECTION GRACE PERIOD TIMEOUT ====================
  useEffect(() => {
    const graceTimer = setTimeout(() => {
      if (mountedRef.current) {
        setConnectionGrace(false);
      }
    }, 5000);
    return () => clearTimeout(graceTimer);
  }, []);

  // ==================== API SERVICE INIT ====================
  useEffect(() => {
    const initStartTime = performance.now();
    apiService.current = new StrategyApiService(
      apiBaseUrl, requestManager.current, dataCache.current
    );
    const initTime = performance.now() - initStartTime;
    console.log(`[MONITOR-PERF] 🔧 API Service initialized in ${initTime.toFixed(2)}ms`);
  }, [apiBaseUrl]);

  // ==================== CLEANUP ====================
  useEffect(() => {
    return () => {
      if (wsUpdateTimeoutRef.current) clearTimeout(wsUpdateTimeoutRef.current);
    };
  }, []);

  // ==================== INITIAL LOAD COMPLETE ====================
  useEffect(() => {
    if (!loading && !initialLoadRef.current && bulkStrategies.length > 0) {
      initialLoadRef.current = true;
      setInitializing(false);
      const initTime = performance.now() - componentMountTime.current;
      console.log(`[MONITOR-PERF] ✅ Initial load complete in ${initTime.toFixed(2)}ms`);
      onInitialLoadComplete?.();
    }
  }, [loading, bulkStrategies.length, onInitialLoadComplete]);

  // ==================== AUTO-CLEAR INITIALIZATION WHEN DATA ARRIVES ====================
  useEffect(() => {
    if (bulkStrategies.length > 0 && initializing) {
      console.log('[MONITOR-PERF] 📦 Data received, clearing initialization state');
      hasDataRef.current = true;
      setInitializing(false);
      setIsOffline(false);
    }
  }, [bulkStrategies.length, initializing]);

  // ==================== ADD LIVE UPDATE ====================
  const addLiveUpdate = useCallback((message, type, data = {}) => {
    if (!mountedRef.current) return;
    const strategyId = data.strategy_id || data.bulk_strategy_id || 'SYSTEM';
    const update = {
      id: Date.now() + Math.random(),
      message: message || 'Update received',
      type: type || 'info',
      timestamp: new Date(),
      strategy_id: strategyId,
      bulk_strategy_id: strategyId,
      symbol: data.symbol,
      signal: data.signal,
      timeframe: data.timeframe,
      raw: data,
    };
    liveUpdatesRef.current = [update, ...liveUpdatesRef.current].slice(0, 50);
    if (type === 'completion' || type === 'bulk_entry' || type === 'bulk_exit') {
      setLiveUpdates([...liveUpdatesRef.current]);
    }
  }, []);

  // ==================== REFRESH TODAY'S TRADES HELPER ====================
  const refreshTodaysTrades = useCallback(() => {
    if (!apiService.current || !mountedRef.current) return;
    apiService.current.loadTodaysTrades(true).then(result => {
      if (mountedRef.current && result) {
        const newTrades = result.trades || [];
        const newSummary = result.summary || {};
        const prev = prevTradesSummaryRef.current;
        const hasChanged = !prev ||
          newTrades.length !== prev.tradeCount ||
          newSummary.total_pnl !== prev.totalPnl ||
          newSummary.total_pnl_pct !== prev.totalPnlPct ||
          newSummary.total_trades !== prev.totalTrades ||
          newSummary.winning_trades !== prev.winningTrades ||
          newSummary.open_trades !== prev.openTrades;
        if (hasChanged) {
          setTodaysTrades(newTrades);
          setTodaysSummary(newSummary);
          prevTradesSummaryRef.current = {
            tradeCount: newTrades.length, totalPnl: newSummary.total_pnl,
            totalPnlPct: newSummary.total_pnl_pct, totalTrades: newSummary.total_trades,
            winningTrades: newSummary.winning_trades, openTrades: newSummary.open_trades,
          };
        }
      }
    }).catch(() => {});
  }, []);

  // ==================== UPDATE STRATEGY FROM WEBSOCKET ====================
  const updateStrategyFromWebSocket = useCallback((wsData) => {
    if (!mountedRef.current) return;
    const wsStartTime = performance.now();
    const messageType = wsData.type;
    console.log(`[WS-UPDATE] Received: ${messageType}`, wsData);

    if (messageType && messageType !== 'unknown') {
      setIsOffline(false);
    }

    // ========== HANDLE STATE_SYNC ==========
    if (messageType === 'state_sync') {
      const activeStrategies = wsData.active_strategies || [];
      const summary = wsData.summary || {};
      console.log(`[WS-UPDATE] state_sync - ${activeStrategies.length} active strategies`);

      if (apiService.current && activeStrategies.length > 0) {
        const transformed = apiService.current.transformAnalyzerResponse({
          summary, active_strategies: activeStrategies, timestamp: wsData.timestamp,
        });
        if (transformed?.strategies) {
          setBulkStrategies(prev => {
            const updatedMap = new Map();
            (Array.isArray(prev) ? prev : []).forEach(s => updatedMap.set(s.bulk_strategy_id, s));
            let anyChanged = prev.length !== transformed.strategies.length;
            transformed.strategies.forEach(s => {
              const existing = updatedMap.get(s.bulk_strategy_id);
              if (existing) {
                const existingStatus = existing.current_state?.status;
                const incomingStatus = s.current_state?.status;

                if (existingStatus === 'open' && incomingStatus !== 'open') {
                  if (incomingStatus !== 'completed' && incomingStatus !== 'idle') {
                    console.log(`[WS-UPDATE] ⛔ Blocked state_sync downgrade: ${s.bulk_strategy_id} ${existingStatus} → ${incomingStatus}`);
                    return;
                  }
                  console.log(`[WS-UPDATE] ✅ Post-exit transition: ${s.bulk_strategy_id} ${existingStatus} → ${incomingStatus}`);
                }

                const statusChanged = existingStatus !== incomingStatus;
                const progressChanged = existing.progress?.percentage !== s.progress?.percentage;
                const entryChanged = existing.current_state?.entry_price !== s.current_state?.entry_price;
                const pendingChanged = (existing.current_state?.pending_confirmations?.length || 0) !==
                                       (s.current_state?.pending_confirmations?.length || 0);
                if (statusChanged || progressChanged || entryChanged || pendingChanged) anyChanged = true;
                updatedMap.set(s.bulk_strategy_id, {
                  ...existing, ...s,
                  current_state: { ...existing.current_state, ...s.current_state },
                  progress: s.progress,
                });
              } else {
                anyChanged = true;
                updatedMap.set(s.bulk_strategy_id, s);
              }
            });
            if (!anyChanged) return prev;
            return Array.from(updatedMap.values());
          });
          setStatusCounts(prev => {
            const next = transformed.statusCounts;
            if (prev && prev.has_data === next.has_data && prev.table_empty === next.table_empty && prev.table_missing === next.table_missing) return prev;
            return next;
          });
          setAnalyzerSummary(prev => {
            if (prev && prev.pending === summary.pending && prev.open === summary.open && prev.idle === summary.idle) return prev;
            return summary;
          });
          setInitializing(false);
          setLoading(false);
        }
      }
      addLiveUpdate(`📊 State sync: ${summary.pending || 0} pending, ${summary.open || 0} open`, 'state_sync', wsData);
      wsMessageTimes.current.push(performance.now() - wsStartTime);
      return;
    }

    // ========== HANDLE PENDING_UPDATE ==========
    if (messageType === 'pending_update') {
      const strategyId = wsData.strategy_id;
      if (!strategyId) return;
      setBulkStrategies(prev => {
        if (!Array.isArray(prev)) return [];
        return prev.map(strategy => {
          if (strategy.bulk_strategy_id !== strategyId) return strategy;
          if (strategy.current_state?.status === 'open') return strategy;
          const completionPct = wsData.completion_percentage || 0;
          const totalSubs = strategy.sub_strategies?.length || 1;
          const completedSubs = Math.round((completionPct / 100) * totalSubs);
          return {
            ...strategy, completion_percentage: completionPct,
            current_state: {
              ...strategy.current_state, status: 'pending', in_position: false,
              completion_percentage: completionPct, trigger_signal: wsData.trigger_signal,
              pending_confirmations: wsData.pending_confirmations || [],
            },
            progress: { completed: completedSubs, total: totalSubs, percentage: completionPct },
          };
        });
      });
      addLiveUpdate(`⏳ ${strategyId}: ${wsData.completion_percentage}% complete`, 'pending_update', wsData);
      wsMessageTimes.current.push(performance.now() - wsStartTime);
      return;
    }

    // ========== HANDLE BULK_ENTRY ==========
    if (messageType === 'bulk_entry') {
      const strategyId = wsData.strategy_id;
      if (!strategyId) return;
      setBulkStrategies(prev => {
        if (!Array.isArray(prev)) return [];
        return prev.map(strategy => {
          if (strategy.bulk_strategy_id !== strategyId) return strategy;
          return {
            ...strategy, completion_percentage: 100,
            current_state: {
              ...strategy.current_state, status: 'open', in_position: true,
              completion_percentage: 100, entry_time: wsData.entry_time,
              entry_price: wsData.entry_price, confirmations: wsData.confirmations,
            },
            progress: {
              completed: strategy.sub_strategies?.length || 1,
              total: strategy.sub_strategies?.length || 1, percentage: 100,
            },
          };
        });
      });

      setAnalyzerSummary(prev => prev ? {
        ...prev,
        open: (prev.open || 0) + 1,
        pending: Math.max(0, (prev.pending || 0) - 1),
      } : prev);

      setStrategyTrades(prev => { const n = { ...prev }; delete n[strategyId]; return n; });
      refreshTodaysTrades();
      addLiveUpdate(`🟢 ENTRY: ${strategyId} @ $${wsData.entry_price}`, 'bulk_entry', wsData);
      wsMessageTimes.current.push(performance.now() - wsStartTime);
      return;
    }

    // ========== HANDLE BULK_EXIT ==========
    if (messageType === 'bulk_exit') {
      const strategyId = wsData.strategy_id;
      if (!strategyId) return;
      setBulkStrategies(prev => {
        if (!Array.isArray(prev)) return [];
        return prev.map(strategy => {
          if (strategy.bulk_strategy_id !== strategyId) return strategy;
          const resetSubStates = {};
          Object.keys(strategy.current_state?.all_sub_strategies_state || {}).forEach(subId => {
            resetSubStates[subId] = { ...strategy.current_state.all_sub_strategies_state[subId], completed: false };
          });
          return {
            ...strategy, completion_percentage: 0,
            current_state: {
              ...strategy.current_state, status: 'completed', in_position: false,
              completion_percentage: 0, exit_time: wsData.exit_time,
              exit_price: wsData.exit_price, pnl: wsData.pnl, pnl_pct: wsData.pnl_pct,
              all_sub_strategies_state: resetSubStates,
            },
            progress: { completed: 0, total: strategy.sub_strategies?.length || 1, percentage: 0 },
            total_complete_trades: (strategy.total_complete_trades || 0) + 1,
          };
        });
      });

      setAnalyzerSummary(prev => prev ? {
        ...prev,
        open: Math.max(0, (prev.open || 0) - 1),
      } : prev);

      setStrategyTrades(prev => { const n = { ...prev }; delete n[strategyId]; return n; });
      refreshTodaysTrades();
      const pnlSign = wsData.pnl >= 0 ? '+' : '';
      const pnlColor = wsData.pnl >= 0 ? '🟢' : '🔴';
      addLiveUpdate(`${pnlColor} EXIT: ${strategyId} @ $${wsData.exit_price} | P&L: ${pnlSign}${wsData.pnl?.toFixed(2)}`, 'bulk_exit', wsData);
      wsMessageTimes.current.push(performance.now() - wsStartTime);
      return;
    }

    // ========== HANDLE ANALYSIS_COMPLETE ==========
    if (messageType === 'analysis_complete') {
      const strategyId = wsData.strategy_id;
      if (!strategyId) return;
      setBulkStrategies(prev => {
        if (!Array.isArray(prev)) return [];
        return prev.map(strategy => {
          if (strategy.bulk_strategy_id !== strategyId) return strategy;
          return { ...strategy, summary: wsData.summary, last_analysis: wsData.timestamp };
        });
      });
      addLiveUpdate(`📊 Analysis: ${strategyId} - ${wsData.summary?.total_trades || 0} trades`, 'analysis_complete', wsData);
      wsMessageTimes.current.push(performance.now() - wsStartTime);
      return;
    }

    // ========== HANDLE STRATEGY_UPDATE (Generic) ==========
    if (messageType === 'strategy_update') {
      const strategyId = wsData.strategy_id;
      if (!strategyId) return;
      setBulkStrategies(prev => {
        if (!Array.isArray(prev)) return [];
        return prev.map(strategy => {
          if (strategy.bulk_strategy_id !== strategyId) return strategy;
          if (strategy.current_state?.status === 'open' && wsData.status && wsData.status !== 'open') {
            if (wsData.status !== 'completed' && wsData.status !== 'idle') {
              return strategy;
            }
          }
          const updatedState = { ...strategy.current_state };
          if (wsData.status) updatedState.status = wsData.status;
          if (wsData.entry_time) updatedState.entry_time = wsData.entry_time;
          if (wsData.entry_price) updatedState.entry_price = wsData.entry_price;
          if (wsData.confirmations) updatedState.confirmations = wsData.confirmations;
          const completionPct = wsData.completion_percentage ?? strategy.progress?.percentage ?? 0;
          const totalSubs = strategy.sub_strategies?.length || 1;
          const completedSubs = Math.round((completionPct / 100) * totalSubs);
          updatedState.completion_percentage = completionPct;
          return {
            ...strategy, completion_percentage: completionPct,
            current_state: updatedState,
            progress: { completed: completedSubs, total: totalSubs, percentage: completionPct },
          };
        });
      });
      addLiveUpdate(`📡 ${strategyId}: ${wsData.status || 'updated'}`, 'strategy_update', wsData);
      wsMessageTimes.current.push(performance.now() - wsStartTime);
      return;
    }

    // ========== HANDLE SIGNAL_UPDATE ==========
    if (messageType === 'signal_update') {
      addLiveUpdate(`🔶 Signal: ${wsData.signal || wsData.data?.signal || 'unknown'}`, 'signal_update', wsData);
      return;
    }

    console.log(`[WS-UPDATE] Unhandled message type: ${messageType}`);
  }, [addLiveUpdate, refreshTodaysTrades]);

  // ==================== WEBSOCKET CONNECTION ====================
  const { isConnected, reconnect: wsReconnect } = useWebSocket(apiBaseUrl, (wsData) => {
    const messageType = wsData.type || 'unknown';
    if (messageType === 'connection' || messageType === 'pong') return;
    if (messageType === 'initial_data') { console.log('[WS] Received initial_data, skipping...'); return; }
    updateStrategyFromWebSocket(wsData);
  });

  // ==================== ✅ FIX: TRACK FIRST CONNECTION & CLEAR OFFLINE/GRACE ====================
  useEffect(() => {
    if (isConnected) {
      hasEverConnected.current = true;
      setConnectionGrace(false);
      if (isOffline) {
        console.log('[MONITOR] WebSocket connected, clearing offline state');
        setIsOffline(false);
      }
    }
  }, [isConnected, isOffline]);

  // ==================== LOAD TODAY'S TRADES ====================
  const loadTodaysTrades = useCallback(async (force = false) => {
    if (!apiService.current || !mountedRef.current) return;
    todaysTradesLoadingRef.current = true;
    try {
      const result = await apiService.current.loadTodaysTrades(force);
      if (!mountedRef.current) return;
      if (result) {
        const newTrades = result.trades || [];
        const newSummary = result.summary || {};
        const prev = prevTradesSummaryRef.current;
        const hasChanged = !prev ||
          newTrades.length !== prev.tradeCount ||
          newSummary.total_pnl !== prev.totalPnl ||
          newSummary.total_pnl_pct !== prev.totalPnlPct ||
          newSummary.total_trades !== prev.totalTrades ||
          newSummary.winning_trades !== prev.winningTrades ||
          newSummary.open_trades !== prev.openTrades;
        if (hasChanged) {
          setTodaysTrades(newTrades);
          setTodaysSummary(newSummary);
          prevTradesSummaryRef.current = {
            tradeCount: newTrades.length, totalPnl: newSummary.total_pnl,
            totalPnlPct: newSummary.total_pnl_pct, totalTrades: newSummary.total_trades,
            winningTrades: newSummary.winning_trades, openTrades: newSummary.open_trades,
          };
        }
        todaysTradesLoadedOnce.current = true;
      }
    } catch (error) {
      if (!mountedRef.current) return;
      console.error('[MONITOR-PERF] ❌ Today\'s trades load failed:', error);
    } finally {
      todaysTradesLoadingRef.current = false;
    }
  }, []);

  // ==================== LOAD CURRENT STATE FROM ANALYZER ====================
  const loadCurrentState = useCallback(async (broadcast = true) => {
    if (!apiService.current || !mountedRef.current) return;
    console.log('[MONITOR-PERF] 🚀 Loading current state from analyzer...');
    loadStartTime.current = performance.now();
    try {
      const result = await apiService.current.loadAllStrategiesCurrentState(broadcast);
      if (!mountedRef.current) return;
      if (result?.strategies) {
        setBulkStrategies(prev => {
          const updatedMap = new Map();
          (Array.isArray(prev) ? prev : []).forEach(s => updatedMap.set(s.bulk_strategy_id, s));
          let anyChanged = prev.length !== result.strategies.length;
          result.strategies.forEach(s => {
            const existing = updatedMap.get(s.bulk_strategy_id);
            if (existing) {
              const existingStatus = existing.current_state?.status;
              const incomingStatus = s.current_state?.status;

              if (existingStatus === 'open' && incomingStatus !== 'open') {
                if (incomingStatus !== 'completed' && incomingStatus !== 'idle') {
                  console.log(`[LOAD-STATE] ⛔ Blocked refresh downgrade: ${s.bulk_strategy_id} ${existingStatus} → ${incomingStatus}`);
                  return;
                }
                console.log(`[LOAD-STATE] ✅ Post-exit transition: ${s.bulk_strategy_id} ${existingStatus} → ${incomingStatus}`);
              }

              const statusChanged = existingStatus !== incomingStatus;
              const progressChanged = existing.progress?.percentage !== s.progress?.percentage;
              const entryChanged = existing.current_state?.entry_price !== s.current_state?.entry_price;
              const pendingChanged = (existing.current_state?.pending_confirmations?.length || 0) !==
                                     (s.current_state?.pending_confirmations?.length || 0);
              if (statusChanged || progressChanged || entryChanged || pendingChanged) anyChanged = true;
              updatedMap.set(s.bulk_strategy_id, {
                ...existing, ...s,
                current_state: { ...existing.current_state, ...s.current_state },
                progress: s.progress,
              });
            } else {
              anyChanged = true;
              updatedMap.set(s.bulk_strategy_id, s);
            }
          });
          if (!anyChanged) return prev;
          return Array.from(updatedMap.values());
        });
        setAnalyzerSummary(prev => {
          const next = result.summary;
          if (prev && prev.pending === next.pending && prev.open === next.open && prev.idle === next.idle && prev.total === next.total) return prev;
          return next;
        });
        setStatusCounts(prev => {
          const next = result.statusCounts;
          if (prev && prev.has_data === next.has_data && prev.table_empty === next.table_empty && prev.table_missing === next.table_missing) return prev;
          return next;
        });
        setIsOffline(false);
        setInitializing(false);
        lastSuccessfulLoadRef.current = {
          strategies: result.strategies, statusCounts: result.statusCounts, timestamp: new Date(),
        };
        const totalTime = performance.now() - loadStartTime.current;
        console.log(`[MONITOR-PERF] ✅ Current state loaded: ${totalTime.toFixed(2)}ms (${result.strategies.length} strategies)`);
        return result;
      }
    } catch (error) {
      if (!mountedRef.current) return;
      console.error('[MONITOR-PERF] ❌ Current state load failed:', error);
      if (!initializing && !isConnected && hasEverConnected.current) {
        const isNetworkError = error.message.includes('fetch') ||
                             error.message.includes('network') ||
                             error.message.includes('timeout') ||
                             error.name === 'TypeError';
        if (isNetworkError) {
          setIsOffline(true);
          if (lastSuccessfulLoadRef.current) {
            addLiveUpdate(`Offline - Using cached data from ${lastSuccessfulLoadRef.current.timestamp.toLocaleTimeString()}`, 'warning', {});
          }
        }
      }
      onLoadError?.(error.message);
      throw error;
    }
  }, [addLiveUpdate, onLoadError, initializing, isConnected]);

  // ==================== LOAD BULK STRATEGIES (Fallback) ====================
  const loadBulkStrategies = useCallback(async (force = false) => {
    if (!apiService.current || !mountedRef.current) return;
    console.log('[MONITOR-PERF] 🚀 Loading bulk strategies...');
    loadStartTime.current = performance.now();
    try {
      const result = await apiService.current.loadBulkStrategies(timeframeFilter, force);
      if (!mountedRef.current) return;
      if (result && Array.isArray(result.strategies)) {
        setBulkStrategies(result.strategies);
        setStatusCounts(result.statusCounts);
        setIsOffline(false);
        setInitializing(false);
        lastSuccessfulLoadRef.current = {
          strategies: result.strategies, statusCounts: result.statusCounts, timestamp: new Date(),
        };
        const totalTime = performance.now() - loadStartTime.current;
        console.log(`[MONITOR-PERF] ✅ Strategies loaded: ${totalTime.toFixed(2)}ms (${result.strategies.length})`);
      }
    } catch (error) {
      if (!mountedRef.current) return;
      console.error('[MONITOR-PERF] ❌ Load failed:', error);
      if (!initializing && !isConnected && hasEverConnected.current) {
        if (lastSuccessfulLoadRef.current) {
          setBulkStrategies(lastSuccessfulLoadRef.current.strategies);
          setStatusCounts(lastSuccessfulLoadRef.current.statusCounts);
          setIsOffline(true);
        }
      }
      onLoadError?.(error.message);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [timeframeFilter, onLoadError, initializing, isConnected]);

  // ==================== LOAD STRATEGY TRADES ====================
  const loadStrategyTrades = useCallback(async (bulkStrategyId) => {
    if (!mountedRef.current || strategyTrades[bulkStrategyId] || tradesLoading[bulkStrategyId]) return;
    if (!apiService.current) return;
    console.log(`[MONITOR-PERF] 📊 Loading trades for ${bulkStrategyId}...`);
    const startTime = performance.now();
    setTradesLoading(prev => ({ ...prev, [bulkStrategyId]: true }));
    try {
      const tradeData = await apiService.current.loadStrategyTrades(bulkStrategyId);
      if (!mountedRef.current) return;
      if (tradeData) {
        setStrategyTrades(prev => ({ ...prev, [bulkStrategyId]: tradeData }));
        console.log(`[MONITOR-PERF] ✅ Trades loaded: ${(performance.now() - startTime).toFixed(2)}ms`);
      }
    } catch (error) {
      if (!mountedRef.current) return;
      console.error(`[MONITOR-PERF] ❌ Trades load failed for ${bulkStrategyId}:`, error);
    } finally {
      if (mountedRef.current) setTradesLoading(prev => ({ ...prev, [bulkStrategyId]: false }));
    }
  }, [strategyTrades, tradesLoading]);

  // ==================== LOAD VALIDATED SEQUENCES ====================
  const loadValidatedSequences = useCallback(async (bulkStrategyId, force = false) => {
    if (!mountedRef.current) return;
    if (!force && strategyBroadcasts[bulkStrategyId]?.validated) return;
    console.log(`[MONITOR-PERF] 📻 Loading broadcasts for ${bulkStrategyId}...`);
    const startTime = performance.now();
    setBroadcastsLoading(prev => ({ ...prev, [bulkStrategyId]: true }));
    try {
      const response = await fetch(
        `${apiBaseUrl}/strategies/api/bulk-strategies/${bulkStrategyId}/validated-broadcasts/`,
        { method: 'GET', headers: { 'Content-Type': 'application/json' }, credentials: 'include' }
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      if (!mountedRef.current) return;
      if (data.success && data.broadcasts) {
        setStrategyBroadcasts(prev => ({
          ...prev,
          [bulkStrategyId]: {
            broadcasts: data.broadcasts, validated: true,
            is_historical: true, data_age: `${data.lookback_days} days`,
          },
        }));
        console.log(`[MONITOR-PERF] ✅ Broadcasts loaded: ${(performance.now() - startTime).toFixed(2)}ms`);
      }
    } catch (error) {
      if (!mountedRef.current) return;
      console.error(`[MONITOR-PERF] ❌ Broadcasts load failed for ${bulkStrategyId}:`, error);
    } finally {
      if (mountedRef.current) setBroadcastsLoading(prev => ({ ...prev, [bulkStrategyId]: false }));
    }
  }, [apiBaseUrl, strategyBroadcasts]);

  // ==================== HANDLERS ====================
  const handleViewTradeHistory = useCallback((bulkStrategyId) => {
    if (!strategyTrades[bulkStrategyId]) loadStrategyTrades(bulkStrategyId);
    navigate(`/officer/trade-history?strategy=${bulkStrategyId}`);
  }, [strategyTrades, loadStrategyTrades, navigate]);

  const handleManualRefresh = useCallback(async () => {
    console.log('[MONITOR-PERF] 🔄 Manual refresh triggered');
    setIsOffline(false);
    setInitializing(false);
    dataCache.current.clear();
    setLoading(true);
    try {
      await Promise.all([loadCurrentState(true), loadTodaysTrades(true)]);
    } catch (error) {
      console.log('[MONITOR-PERF] Falling back to bulk strategies endpoint...');
      await loadBulkStrategies(true);
    } finally {
      setLoading(false);
    }
  }, [loadCurrentState, loadBulkStrategies, loadTodaysTrades]);

  const handleManualSync = useCallback(async () => {
    const strategiesToSync = Array.isArray(bulkStrategies) ? bulkStrategies : [];
    if (!apiService.current || strategiesToSync.length === 0) {
      setSyncStatus({ type: 'warning', message: 'No strategies to sync' }); return;
    }
    if (isOffline) {
      setSyncStatus({ type: 'error', message: 'Cannot sync while offline' }); return;
    }
    console.log(`[MONITOR-PERF] 🔄 Syncing ${strategiesToSync.length} strategies...`);
    const startTime = performance.now();
    setSyncing(true);
    setSyncStatus({ type: 'info', message: `Syncing ${strategiesToSync.length} strategies...` });
    try {
      const promises = strategiesToSync.map(strategy =>
        apiService.current.syncStrategy(strategy.bulk_strategy_id)
      );
      const results = await Promise.all(promises);
      const successCount = results.filter(r => r.success).length;
      const totalTime = performance.now() - startTime;
      if (!mountedRef.current) return;
      setSyncStatus({
        type: successCount === strategiesToSync.length ? 'success' : 'warning',
        message: `Synced ${successCount}/${strategiesToSync.length} strategies in ${totalTime.toFixed(0)}ms`,
      });
      setLastSync(new Date());
      await loadCurrentState(true);
    } catch (error) {
      if (!mountedRef.current) return;
      console.error('[MONITOR-PERF] ❌ Sync failed:', error);
      setSyncStatus({ type: 'error', message: `Sync failed: ${error.message}` });
    } finally {
      if (mountedRef.current) setSyncing(false);
    }
  }, [bulkStrategies, loadCurrentState, isOffline]);

  const handleComputeAll = useCallback(async () => {
    if (!apiService.current || isOffline) {
      addLiveUpdate('Cannot compute states while offline', 'error', {}); return;
    }
    console.log('[MONITOR-PERF] 🔄 Computing all strategies...');
    const startTime = performance.now();
    setLoading(true);
    addLiveUpdate('Fetching current state from analyzer (will broadcast)...', 'info', {});
    try {
      const result = await loadCurrentState(true);
      const totalTime = performance.now() - startTime;
      console.log(`[MONITOR-PERF] ✅ Compute complete: ${totalTime.toFixed(2)}ms`);
      if (result?.summary) {
        addLiveUpdate(`✅ Loaded ${result.summary.pending} pending, ${result.summary.open} open strategies`, 'success', {});
      }
    } catch (error) {
      console.error('[MONITOR-PERF] ❌ Compute failed:', error);
      addLiveUpdate(`Compute failed: ${error.message}`, 'error', {});
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [isOffline, addLiveUpdate, loadCurrentState]);

  const handleCardExpand = useCallback((strategyId) => {
    setExpandedCards(prev => ({ ...prev, [strategyId]: !prev[strategyId] }));
    if (!strategyBroadcasts[strategyId] && !isOffline) {
      loadValidatedSequences(strategyId, false);
    }
  }, [strategyBroadcasts, loadValidatedSequences, isOffline]);

  // ==================== INITIALIZATION ====================
  useEffect(() => {
    const initializeMonitor = async () => {
      if (!mountedRef.current) return;
      console.log('[MONITOR-PERF] 🚀 Initializing monitor...');
      const startTime = performance.now();
      setLoading(true);
      setInitializing(true);
      try {
        await Promise.all([loadCurrentState(true), loadTodaysTrades(false)]);
      } catch (error) {
        console.log('[MONITOR-PERF] Analyzer failed, trying bulk strategies...');
        try { await loadBulkStrategies(true); }
        catch (error2) { console.log('[MONITOR-PERF] Both endpoints failed, waiting for WebSocket...'); }
      } finally {
        if (mountedRef.current) {
          setLoading(false);
          console.log(`[MONITOR-PERF] ✅ Initialization attempt complete: ${(performance.now() - startTime).toFixed(2)}ms`);
        }
      }
    };
    initializeMonitor();
    initializationTimeoutRef.current = setTimeout(() => {
      if (mountedRef.current && !hasDataRef.current) {
        console.log('[MONITOR-PERF] ⚠️ Initialization timeout - no data received');
        setInitializing(false);
        if (!isConnected && hasEverConnected.current) {
          setIsOffline(true);
        }
      }
    }, 10000);
    return () => {
      dataCache.current.clear();
      if (initializationTimeoutRef.current) clearTimeout(initializationTimeoutRef.current);
    };
  }, []);

  // ==================== PERIODIC REFRESH ====================
  useEffect(() => { loadingRef.current = loading; }, [loading]);

  useEffect(() => {
    if (isOffline || !isConnected) return;
    const refreshInterval = setInterval(() => {
      if (mountedRef.current && !loadingRef.current) {
        console.log('[MONITOR-PERF] 🔄 Periodic refresh...');
        loadCurrentState(false).catch(() => {});
      }
    }, 30000);
    return () => clearInterval(refreshInterval);
  }, [isOffline, isConnected, loadCurrentState]);

  // ==================== PERIODIC TRADES REFRESH ====================
  useEffect(() => {
    if (isOffline) return;
    const tradesRefreshInterval = setInterval(() => {
      if (mountedRef.current) loadTodaysTrades(true).catch(() => {});
    }, 60000);
    return () => clearInterval(tradesRefreshInterval);
  }, [isOffline, loadTodaysTrades]);

  // ==================== HELPER: Get completion % ====================
  const getStrategyCompletionPct = useCallback((s) => {
    return s.progress?.percentage 
      ?? s.current_state?.completion_percentage 
      ?? (s.progress?.total > 0 ? (s.progress.completed / s.progress.total) * 100 : 0);
  }, []);

  // ==================== HIDDEN BY TIME COUNT ====================
  const hiddenByTimeCount = useMemo(() => {
    const strategies = Array.isArray(bulkStrategies) ? bulkStrategies : [];
    return strategies.length - strategies.filter(s => isStrategyActiveNow(s)).length;
  }, [bulkStrategies]);

  // ==================== FILTERED STRATEGIES ====================
  // FIX: When a timeframe filter is active, open and pending strategies are
  // always shown regardless of their timeframe. This ensures QQQ 45s strategies
  // (or any cross-timeframe strategy) appear on the monitor when they fire.
  const filteredStrategies = useMemo(() => {
    const strategies = Array.isArray(bulkStrategies) ? bulkStrategies : [];
    let filtered = strategies.filter(s => isStrategyActiveNow(s));

    if (timeframeFilter !== 'all') {
      filtered = filtered.filter(s => {
        // Always show open and pending strategies regardless of timeframe
        const status = s.current_state?.status;
        if (status === 'open' || status === 'pending') return true;
        return s.timeframe === timeframeFilter;
      });
    }

    if (pendingFilter) {
      filtered = filtered.filter(s => {
        const status = s.current_state?.status || s.status;
        if (status !== 'pending') return false;
        return getStrategyCompletionPct(s) >= PENDING_THRESHOLD;
      });
    }
    if (positionFilter) {
      filtered = filtered.filter(s => {
        const progress = s.progress?.total > 0
          ? (s.progress.completed / s.progress.total) * 100
          : s.progress?.percentage || 0;
        return progress === 100;
      });
    }
    return filtered.sort((a, b) => {
      const aHot = a.current_state?.status === 'pending' && (a.current_state?.pending_confirmations?.length === 1);
      const bHot = b.current_state?.status === 'pending' && (b.current_state?.pending_confirmations?.length === 1);
      if (aHot && !bHot) return -1;
      if (!aHot && bHot) return 1;
      const aOpen = a.current_state?.status === 'open';
      const bOpen = b.current_state?.status === 'open';
      if (aOpen && !bOpen) return -1;
      if (!aOpen && bOpen) return 1;
      const aPending = a.current_state?.status === 'pending';
      const bPending = b.current_state?.status === 'pending';
      if (aPending && !bPending) return -1;
      if (!aPending && bPending) return 1;
      if (aPending && bPending) return getStrategyCompletionPct(b) - getStrategyCompletionPct(a);
      const nameA = (a.name || a.bulk_strategy_id || '').toLowerCase();
      const nameB = (b.name || b.bulk_strategy_id || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [bulkStrategies, timeframeFilter, positionFilter, pendingFilter, getStrategyCompletionPct]);

  // ==================== TIMEFRAME GROUPS ====================
  const timeframeGroups = useMemo(() => {
    const groups = {};
    const strategies = Array.isArray(bulkStrategies) ? bulkStrategies : [];
    strategies.forEach(strategy => {
      if (isStrategyActiveNow(strategy)) {
        const tf = strategy.timeframe || 'unknown';
        if (!groups[tf]) groups[tf] = [];
        groups[tf].push(strategy);
      }
    });
    return groups;
  }, [bulkStrategies]);

  // ==================== SUMMARY STATISTICS ====================
  const summary = useMemo(() => {
    const allStrategies = Array.isArray(bulkStrategies) ? bulkStrategies : [];
    const visibleStrategies = allStrategies.filter(s => isStrategyActiveNow(s));
    const strategies = timeframeFilter === 'all'
      ? visibleStrategies
      : visibleStrategies.filter(s => s.timeframe === timeframeFilter);
    const with100Percent = strategies.filter(s => {
      const progress = s.progress?.total > 0
        ? (s.progress.completed / s.progress.total) * 100 : s.progress?.percentage || 0;
      return progress === 100;
    }).length;
    const pendingQualified = strategies.filter(s => {
      const status = s.current_state?.status || s.status;
      if (status !== 'pending') return false;
      return getStrategyCompletionPct(s) >= PENDING_THRESHOLD;
    }).length;
    const hotCount = strategies.filter(s => {
      const status = s.current_state?.status || s.status;
      return status === 'pending' && s.current_state?.pending_confirmations?.length === 1;
    }).length;

    const liveOpenCount = strategies.filter(s => s.current_state?.status === 'open').length;
    const livePendingCount = strategies.filter(s => s.current_state?.status === 'pending').length;

    return {
      total: strategies.length,
      total_all_timeframes: visibleStrategies.length,
      active: strategies.filter(s => s.is_active).length,
      in_position: with100Percent,
      pending: livePendingCount,
      pending_qualified: pendingQualified,
      hot: hotCount,
      open: liveOpenCount,
      completed_strategies: strategies.reduce((sum, s) => sum + (s.progress?.completed || 0), 0),
      total_sub_strategies: strategies.reduce((sum, s) => sum + (s.progress?.total || 0), 0),
      timeframes: Object.keys(timeframeGroups).sort(),
      with_data: strategies.filter(s => s.has_data !== false).length,
      without_data: strategies.filter(s => s.has_data === false).length,
    };
  }, [bulkStrategies, timeframeFilter, timeframeGroups, getStrategyCompletionPct]);

  // ==================== TODAY'S TRADES COMPUTED VALUES ====================
  const todaysTradesComputed = useMemo(() => {
    if (!todaysSummary) {
      return { totalPnl: 0, totalPnlPct: 0, totalTrades: 0, winningTrades: 0, losingTrades: 0, winRate: 0, pnlPositive: true };
    }
    const totalPnl = todaysSummary.total_pnl || 0;
    const totalPnlPct = todaysSummary.total_pnl_pct || 0;
    return {
      totalPnl, totalPnlPct,
      totalTrades: todaysSummary.total_trades || 0,
      winningTrades: todaysSummary.winning_trades || 0,
      losingTrades: todaysSummary.losing_trades || 0,
      winRate: todaysSummary.win_rate || 0,
      pnlPositive: totalPnl >= 0,
    };
  }, [todaysSummary]);

  const strategiesCount = summary.total_all_timeframes;

  // ==================== FORMAT HELPERS ====================
  const formatPnl = (v) => {
    if (v == null) return '—';
    const sign = v >= 0 ? '+' : '';
    return `${sign}$${Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };
  const formatPnlPct = (v) => {
    if (v == null) return '—';
    const sign = v >= 0 ? '+' : '';
    return `${sign}${v.toFixed(2)}%`;
  };
  const formatPrice = (v) => {
    if (v == null) return '—';
    return `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };
  const formatTradeTime = (t) => {
    if (!t) return '—';
    try {
      // Timestamps are already stored in ET by the backend webhook ingestion.
      // Extract HH:MM:SS directly from the ISO string — no Date object, no
      // timezone conversion, otherwise the browser double-converts.
      const match = t.match(/T(\d{2}):(\d{2}):(\d{2})/);
      if (match) {
        let h = parseInt(match[1], 10);
        const m = match[2];
        const s = match[3];
        const period = h >= 12 ? 'PM' : 'AM';
        return (h % 12 || 12) + ':' + m + ':' + s + ' ' + period;
      }
      return t;
    } catch { return t; }
  };

  // ==================== LOADING STATE ====================
  const showLoading = loading || (initializing && bulkStrategies.length === 0 && !isOffline);
  
  if (showLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', position: 'relative', zIndex: 0 }}>
        <Stack spacing={2} alignItems="center">
          <CircularProgress size={48} sx={{ color: '#764ba2' }} />
          <Typography sx={{ color: alpha('#fff', 0.5) }}>
            {initializing ? 'Connecting to server...' : 'Loading strategies...'}
          </Typography>
        </Stack>
      </Box>
    );
  }

  // ==================== CONNECTION STATUS HELPERS ====================
  const connectionLabel = isConnected ? 'LIVE' : connectionGrace ? 'CONNECTING...' : 'OFFLINE';
  const connectionColor = isConnected ? '#00ff88' : connectionGrace ? '#f59e0b' : '#ff4757';

  // ==================== MAIN RENDER ====================
  return (
    <Box sx={{ p: 3, minHeight: '100vh', position: 'relative', zIndex: 0 }}>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5, mb: 3,
          bgcolor: alpha('#1a1f2e', 0.8),
          border: `1px solid ${alpha(isOffline && !connectionGrace ? '#ff4757' : '#00d4ff', 0.2)}`,
          borderRadius: 2, position: 'relative', zIndex: 0,
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Avatar sx={{ width: 56, height: 56, bgcolor: alpha('#764ba2', 0.2), border: `1px solid ${alpha('#764ba2', 0.3)}` }}>
              <TimelineIcon sx={{ fontSize: 28 }} />
            </Avatar>
            <Box>
              <Typography variant="h5" sx={{ color: '#fff', fontWeight: 700, mb: 0.5 }}>
                Bulk Strategy Monitor
              </Typography>
              <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                <Chip
                  icon={<DotIcon sx={{ fontSize: 12 }} />}
                  label={connectionLabel}
                  size="small"
                  sx={{
                    bgcolor: alpha(connectionColor, 0.15),
                    color: connectionColor,
                    fontWeight: 600, height: 22,
                  }}
                />
                {isOffline && !connectionGrace && (
                  <Chip
                    icon={<WifiOffIcon sx={{ fontSize: 12 }} />}
                    label="NO INTERNET"
                    size="small"
                    sx={{ bgcolor: alpha('#ff4757', 0.15), color: '#ff4757', fontWeight: 600, height: 22 }}
                  />
                )}
                {syncing && (
                  <Chip
                    icon={<CircularProgress size={12} sx={{ color: '#f59e0b' }} />}
                    label="SYNCING"
                    size="small"
                    sx={{ bgcolor: alpha('#f59e0b', 0.15), color: '#f59e0b', fontWeight: 600, height: 22 }}
                  />
                )}
                {analyzerSummary && (
                  <Chip
                    icon={<BoltIcon sx={{ fontSize: 14 }} />}
                    label={`${summary.pending || 0} PENDING | ${summary.open || 0} OPEN`}
                    size="small"
                    sx={{ bgcolor: alpha('#10b981', 0.15), color: '#10b981', fontWeight: 600, height: 22 }}
                  />
                )}
                {summary.hot > 0 && (
                  <Chip
                    icon={<FireIcon sx={{ fontSize: 14 }} />}
                    label={`${summary.hot} HOT`}
                    size="small"
                    sx={{ bgcolor: alpha('#ff6b35', 0.15), color: '#ff6b35', fontWeight: 700, height: 22 }}
                  />
                )}
                {statusCounts && (
                  <Tooltip title={`${statusCounts.has_data} with data`}>
                    <Chip
                      icon={<StorageIcon sx={{ fontSize: 14 }} />}
                      label={`${statusCounts.has_data}/${strategiesCount} DATA`}
                      size="small"
                      sx={{ bgcolor: alpha('#764ba2', 0.15), color: '#764ba2', fontWeight: 600, height: 22 }}
                    />
                  </Tooltip>
                )}
                <Typography variant="caption" sx={{ color: alpha('#fff', 0.5), fontSize: '0.75rem' }}>
                  {strategiesCount} strategies active ({summary.timeframes.join(', ')})
                  {hiddenByTimeCount > 0 && (
                    <> • <span style={{ color: '#ffa502' }}>{hiddenByTimeCount} hidden</span></>
                  )} • 
                  NY: <NYClock /> • 
                  Last sync: {lastSync?.toLocaleTimeString() || 'Never'}
                </Typography>
              </Stack>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <ToggleButtonGroup
              value={timeframeFilter}
              exclusive
              onChange={(e, newFilter) => {
                if (newFilter !== null) {
                  setTimeframeFilter(newFilter);
                  setPositionFilter(false);
                  setPendingFilter(false);
                }
              }}
              size="small"
              sx={{
                bgcolor: alpha('#1a1f2e', 0.6),
                '& .MuiToggleButton-root': {
                  color: alpha('#fff', 0.5),
                  border: `1px solid ${alpha('#00d4ff', 0.2)}`,
                  px: 2,
                  '&.Mui-selected': { bgcolor: alpha('#00d4ff', 0.2), color: '#00d4ff', fontWeight: 700 },
                },
              }}
            >
              <ToggleButton value="all">ALL ({strategiesCount})</ToggleButton>
              {summary.timeframes.map(tf => (
                <ToggleButton key={tf} value={tf}>
                  <Badge
                    badgeContent={
                      (timeframeGroups[tf] || []).filter(s => isStrategyActiveNow(s)).length || 0
                    }
                    color="primary"
                    sx={{ '& .MuiBadge-badge': { right: -12, top: -4 } }}
                  >
                    {tf}
                  </Badge>
                </ToggleButton>
              ))}
            </ToggleButtonGroup>

            <Tooltip title="View trade history">
              <Button variant="outlined" size="small" startIcon={<HistoryIcon />}
                onClick={() => navigate('/officer/trade-history')}
                sx={{ borderColor: alpha('#00d4ff', 0.3), color: '#00d4ff', fontWeight: 600, '&:hover': { borderColor: '#00d4ff', bgcolor: alpha('#00d4ff', 0.1) } }}>
                History
              </Button>
            </Tooltip>
            <Tooltip title="Load current state from analyzer & broadcast">
              <Button variant="outlined" size="small" startIcon={<BoltIcon />}
                onClick={handleComputeAll} disabled={loading || isOffline}
                sx={{ borderColor: alpha('#10b981', 0.3), color: '#10b981', fontWeight: 600,
                  '&:hover': { borderColor: '#10b981', bgcolor: alpha('#10b981', 0.1) },
                  '&:disabled': { borderColor: alpha('#666', 0.2), color: '#666' } }}>
                Analyze All
              </Button>
            </Tooltip>
            <Tooltip title={isOffline ? 'Cannot sync while offline' : 'Sync from database'}>
              <span>
                <IconButton size="small" onClick={handleManualSync} disabled={syncing || isOffline}
                  sx={{
                    bgcolor: alpha(isOffline ? '#666' : '#f59e0b', 0.15),
                    border: `1px solid ${alpha(isOffline ? '#666' : '#f59e0b', 0.3)}`,
                    '&:hover': { bgcolor: alpha(isOffline ? '#666' : '#f59e0b', 0.25) },
                    '&:disabled': { bgcolor: alpha('#666', 0.1) },
                  }}>
                  <SyncIcon sx={{ color: (syncing || isOffline) ? '#666' : '#f59e0b', fontSize: 20 }} />
                </IconButton>
              </span>
            </Tooltip>
            <Button variant="outlined" size="small" startIcon={<RefreshIcon />}
              onClick={handleManualRefresh}
              sx={{ borderColor: alpha('#764ba2', 0.3), color: '#764ba2', fontWeight: 600,
                '&:hover': { borderColor: '#764ba2', bgcolor: alpha('#764ba2', 0.1) } }}>
              {isOffline ? 'Retry' : 'Refresh'}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* Alerts */}
      {isOffline && !connectionGrace && (
        <Alert severity="warning" icon={<WifiOffIcon />}
          sx={{ mb: 3, bgcolor: alpha('#ff4757', 0.1), border: `1px solid ${alpha('#ff4757', 0.3)}` }}>
          <AlertTitle sx={{ fontWeight: 700 }}>Offline Mode</AlertTitle>
          {lastSuccessfulLoadRef.current ? (
            <>Network connection lost. Showing cached data from {lastSuccessfulLoadRef.current.timestamp.toLocaleTimeString()}. Click "Retry" to reconnect.</>
          ) : (<>No internet connection. Unable to load strategies.</>)}
        </Alert>
      )}

      {syncStatus && (
        <Collapse in={!!syncStatus}>
          <Alert severity={syncStatus.type}
            icon={syncStatus.type === 'success' ? <CheckCircleIcon /> : <WarningIcon />}
            onClose={() => setSyncStatus(null)}
            sx={{
              mb: 3,
              bgcolor: alpha(syncStatus.type === 'success' ? '#10b981' : syncStatus.type === 'error' ? '#ff4757' : '#f59e0b', 0.1),
              border: `1px solid ${alpha(syncStatus.type === 'success' ? '#10b981' : syncStatus.type === 'error' ? '#ff4757' : '#f59e0b', 0.3)}`,
            }}>
            <AlertTitle sx={{ fontWeight: 700 }}>
              {syncStatus.type === 'success' ? 'Sync Complete' : syncStatus.type === 'error' ? 'Sync Failed' : 'Sync Status'}
            </AlertTitle>
            {syncStatus.message}
          </Alert>
        </Collapse>
      )}

      {hiddenByTimeCount > 0 && (
        <Alert severity="info" icon={<ScheduleIcon />}
          sx={{ mb: 3, bgcolor: alpha('#ffa502', 0.05), border: `1px solid ${alpha('#ffa502', 0.2)}` }}>
          <AlertTitle sx={{ fontWeight: 700 }}>
            {hiddenByTimeCount} {hiddenByTimeCount === 1 ? 'Strategy' : 'Strategies'} Outside Trading Hours
          </AlertTitle>
          {hiddenByTimeCount} {hiddenByTimeCount === 1 ? 'strategy is' : 'strategies are'} hidden because {hiddenByTimeCount === 1 ? "it's" : "they're"} outside configured trading hours.
        </Alert>
      )}

      {statusCounts && (statusCounts.table_empty > 0 || statusCounts.table_missing > 0) && (
        <Alert severity="info" icon={<CloudOffIcon />}
          sx={{ mb: 3, bgcolor: alpha('#00d4ff', 0.05), border: `1px solid ${alpha('#00d4ff', 0.2)}` }}>
          <AlertTitle sx={{ fontWeight: 700 }}>Some Strategies Have No Data</AlertTitle>
          {statusCounts.table_missing > 0 && (
            <Typography variant="body2">{statusCounts.table_missing} strategies have missing database tables.</Typography>
          )}
          {statusCounts.table_empty > 0 && (
            <Typography variant="body2">{statusCounts.table_empty} strategies have empty database tables.</Typography>
          )}
        </Alert>
      )}

      {/* ==================== STAT CARDS ==================== */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: { xs: 'wrap', md: 'nowrap' }, maxWidth: '1440px' }}>
        <Box sx={{ flex: { xs: '1 1 45%', md: '1 1 0' }, minWidth: 0 }}>
          <StatCard title="Pending" value={summary.pending_qualified} color="#f59e0b" icon={ScheduleIcon}
            subtitle={`≥${PENDING_THRESHOLD}% Complete`}
            onClick={() => { setPendingFilter(!pendingFilter); setPositionFilter(false); }}
            isActive={pendingFilter} />
        </Box>
        <Box sx={{ flex: { xs: '1 1 45%', md: '1 1 0' }, minWidth: 0 }}>
          <StatCard title="Open" value={summary.open} color="#00ff88" icon={BoltIcon}
            subtitle="In Position"
            onClick={() => { setPositionFilter(!positionFilter); setPendingFilter(false); }}
            isActive={positionFilter} />
        </Box>
        <Box sx={{ flex: { xs: '1 1 45%', md: '1 1 0' }, minWidth: 0 }}>
          <StatCard title="Today's P&L"
            value={formatPnlPct(todaysTradesComputed.totalPnlPct)}
            color={todaysTradesComputed.pnlPositive ? '#10b981' : '#ef4444'}
            icon={WalletIcon}
            subtitle={`${formatPnl(todaysTradesComputed.totalPnl)} • ${todaysTradesComputed.totalTrades} Trade${todaysTradesComputed.totalTrades !== 1 ? 's' : ''}`} />
        </Box>
        <Box sx={{ flex: { xs: '1 1 45%', md: '1 1 0' }, minWidth: 0 }}>
          <StatCard title="Win / Loss"
            value={`${todaysTradesComputed.winningTrades} / ${todaysTradesComputed.losingTrades}`}
            color="#eab308" icon={TrophyIcon} subtitle="Today" />
        </Box>
        <Box sx={{ flex: { xs: '1 1 45%', md: '1 1 0' }, minWidth: 0 }}>
          {summary.hot > 0 ? (
            <StatCard title="🔥 Hot" value={summary.hot} color="#ff6b35" icon={FireIcon} subtitle="1 Signal Away" />
          ) : (
            <StatCard title="Total" value={summary.total} color="#00d4ff" icon={DashboardIcon}
              subtitle={timeframeFilter === 'all' ? 'All Timeframes' : timeframeFilter}
              onClick={() => { setPositionFilter(false); setPendingFilter(false); setTimeframeFilter('all'); }}
              isActive={!positionFilter && !pendingFilter && timeframeFilter === 'all'} />
          )}
        </Box>
      </Box>

      {/* ==================== TODAY'S TRADES TABLE ==================== */}
      <Paper elevation={0}
        sx={{
          mb: 3, bgcolor: alpha('#1a1f2e', 0.8),
          border: `1px solid ${alpha('#334155', 0.5)}`,
          borderRadius: 2, overflow: 'hidden',
          display: todaysTrades.length > 0 ? 'block' : 'none',
        }}>
        <Box onClick={() => setTodaysTradesExpanded(!todaysTradesExpanded)}
          sx={{
            px: 2.5, py: 1.5,
            borderBottom: todaysTradesExpanded ? `1px solid ${alpha('#334155', 0.3)}` : 'none',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            cursor: 'pointer', '&:hover': { bgcolor: alpha('#fff', 0.02) },
          }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '0.95rem' }}>📅 Today's Trades</Typography>
            <Chip label={todaysTrades.length} size="small"
              sx={{ bgcolor: alpha('#8b5cf6', 0.2), color: '#8b5cf6', fontWeight: 700, fontSize: '0.75rem', height: 22 }} />
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography sx={{ color: todaysTradesComputed.pnlPositive ? '#10b981' : '#ef4444', fontWeight: 700, fontSize: '0.9rem' }}>
              Net: {formatPnlPct(todaysTradesComputed.totalPnlPct)} ({formatPnl(todaysTradesComputed.totalPnl)})
            </Typography>
            {todaysTradesExpanded ? (
              <ExpandLessIcon sx={{ color: alpha('#fff', 0.5), fontSize: 20 }} />
            ) : (
              <ExpandMoreIcon sx={{ color: alpha('#fff', 0.5), fontSize: 20 }} />
            )}
          </Stack>
        </Box>
        <Collapse in={todaysTradesExpanded}>
          <TableContainer sx={{ maxHeight: 400 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {['Strategy', 'Direction', 'Entry', 'Exit', 'P&L', 'Entry Time', 'Exit Time', 'Status'].map(header => (
                    <TableCell key={header} sx={{
                      bgcolor: alpha('#0f172a', 0.95), color: alpha('#fff', 0.5),
                      fontSize: '0.65rem', fontWeight: 600, textTransform: 'uppercase',
                      letterSpacing: '0.05em', borderBottom: `1px solid ${alpha('#334155', 0.2)}`, py: 1,
                    }}>{header}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {todaysTrades.map((trade, idx) => {
                  const isWin = trade.pnl != null && trade.pnl > 0;
                  const isOpenTrade = trade.status === 'open';
                  return (
                    <TableRow key={`${trade.bulk_strategy_id}-${idx}`}
                      sx={{ '&:hover': { bgcolor: alpha('#fff', 0.02) }, borderBottom: `1px solid ${alpha('#334155', 0.1)}` }}>
                      <TableCell sx={{ color: '#fff', fontWeight: 600, fontSize: '0.8rem', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', borderBottom: 'none' }}>
                        {trade.bulk_strategy_id}
                      </TableCell>
                      <TableCell sx={{ borderBottom: 'none' }}>
                        <Chip label={trade.direction} size="small"
                          sx={{ bgcolor: alpha(trade.direction === 'LONG' ? '#10b981' : '#ef4444', 0.15),
                            color: trade.direction === 'LONG' ? '#10b981' : '#ef4444', fontWeight: 700, fontSize: '0.65rem', height: 20 }} />
                      </TableCell>
                      <TableCell sx={{ color: '#94a3b8', fontSize: '0.8rem', borderBottom: 'none' }}>
                        {formatPrice(trade.entry_price)}
                      </TableCell>
                      <TableCell sx={{ color: '#94a3b8', fontSize: '0.8rem', borderBottom: 'none' }}>
                        {trade.exit_price ? formatPrice(trade.exit_price) : '—'}
                      </TableCell>
                      <TableCell sx={{ borderBottom: 'none' }}>
                        <Stack direction="row" spacing={0.5} alignItems="baseline">
                          <Typography sx={{ color: isOpenTrade ? '#f59e0b' : isWin ? '#10b981' : '#ef4444', fontWeight: 700, fontSize: '0.8rem' }}>
                            {trade.pnl != null ? formatPnl(trade.pnl) : '—'}
                          </Typography>
                          {trade.pnl_pct != null && (
                            <Typography sx={{ color: alpha('#fff', 0.4), fontSize: '0.7rem' }}>
                              ({trade.pnl_pct > 0 ? '+' : ''}{trade.pnl_pct.toFixed(2)}%)
                            </Typography>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ color: alpha('#fff', 0.5), fontSize: '0.75rem', borderBottom: 'none' }}>
                        {formatTradeTime(trade.entry_time)}
                      </TableCell>
                      <TableCell sx={{ color: alpha('#fff', 0.5), fontSize: '0.75rem', borderBottom: 'none' }}>
                        {trade.exit_time ? formatTradeTime(trade.exit_time) : isOpenTrade ? '⚡ Open' : '—'}
                      </TableCell>
                      <TableCell sx={{ borderBottom: 'none' }}>
                        <Chip label={isOpenTrade ? '⚡ LIVE' : isWin ? '✅ WIN' : '❌ LOSS'} size="small"
                          sx={{ bgcolor: alpha(isOpenTrade ? '#10b981' : isWin ? '#10b981' : '#ef4444', 0.15),
                            color: isOpenTrade ? '#10b981' : isWin ? '#10b981' : '#ef4444', fontWeight: 700, fontSize: '0.65rem', height: 22 }} />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Collapse>
      </Paper>

      {/* Strategy Cards Grid */}
      <Grid container spacing={2} sx={{ justifyContent: 'flex-start' }}>
        {filteredStrategies.map((strategy) => (
          <Grid item xs={12} md={6} lg={6} xl={4} key={strategy.bulk_strategy_id}
            sx={{
              flexBasis: { md: '45%', lg: '45%', xl: '30%' },
              maxWidth: { md: '45% !important', lg: '45% !important', xl: '30% !important' },
            }}>
            <BulkStrategyCard
              strategy={strategy}
              isExpanded={expandedCards[strategy.bulk_strategy_id] || false}
              onExpandChange={handleCardExpand}
              onOpenDoc={() => {}}
              onViewTradeHistory={handleViewTradeHistory}
              liveUpdates={liveUpdatesRef.current}
              broadcasts={strategyBroadcasts[strategy.bulk_strategy_id]?.broadcasts || []}
              broadcastsLoading={broadcastsLoading[strategy.bulk_strategy_id] || false}
              isHistorical={strategyBroadcasts[strategy.bulk_strategy_id]?.is_historical || false}
              dataAge={strategyBroadcasts[strategy.bulk_strategy_id]?.data_age || '24 hours'}
              trades={strategyTrades[strategy.bulk_strategy_id]?.trades || []}
              tradesLoading={tradesLoading[strategy.bulk_strategy_id] || false}
            />
          </Grid>
        ))}
      </Grid>

      {/* Empty state when pending filter is active but no results */}
      {pendingFilter && filteredStrategies.length === 0 && (
        <Paper elevation={0}
          sx={{ p: 4, mt: 2, textAlign: 'center', bgcolor: alpha('#1a1f2e', 0.8),
            border: `1px dashed ${alpha('#f59e0b', 0.3)}`, borderRadius: 2 }}>
          <ScheduleIcon sx={{ fontSize: 48, color: alpha('#f59e0b', 0.4), mb: 2 }} />
          <Typography sx={{ color: alpha('#fff', 0.6), fontSize: '1rem', mb: 1 }}>
            No Pending Strategies Meet the Threshold
          </Typography>
          <Typography sx={{ color: alpha('#fff', 0.4), fontSize: '0.85rem' }}>
            No strategies are currently pending with ≥{PENDING_THRESHOLD}% signal completion.
            {hiddenByTimeCount > 0 && ` (${hiddenByTimeCount} strategies are outside trading hours)`}
          </Typography>
          <Button variant="outlined" size="small" onClick={() => setPendingFilter(false)}
            sx={{ mt: 2, borderColor: alpha('#f59e0b', 0.3), color: '#f59e0b',
              '&:hover': { borderColor: '#f59e0b', bgcolor: alpha('#f59e0b', 0.1) } }}>
            Show All Strategies
          </Button>
        </Paper>
      )}
    </Box>
  );
};

export default BulkStrategyMonitor;
